import { Link } from 'Components/Core';
import { useEffect, useState } from 'react';
import { MdRemoveCircle } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../Data/ReduxViewModels/Admin';
import { Button, ButtonGroup, Checkbox, ConfirmDialog, Field, Message, PlaceholderButton } from '../Core';
import { FieldSet, ListFieldSection, PageSection } from '../Layout';
import { DatabaseConnectionSection } from './Core/Sections';

const FilterSection = ({ handleClick, showSection }) => {
  const integrationConnections = useSelector((state) => state.admin.syncModel.IntegrationDbConnections);
  const [disableCheckbox, setDisableCheckbox] = useState(false);

  useEffect(() => {
    setDisableCheckbox(integrationConnections ? integrationConnections.length > 0 : false);
  }, [integrationConnections]);

  const dispatch = useDispatch();
  const addRemoveHiveConnection = (checked) => {
    if (checked) {
      dispatch(
        actions.setValue({
          name: 'syncModel.HivePublishConnection',
          value: {
            Name: 'HivePublish_Data',
            DataSource: '',
            InitialCatalog: '',
            IntegratedSecurity: 'true',
            IsAssisted: 'true',
            CustomerProviderSelection: 'System.Data.SqlClient',
          },
          sync: true,
        })
      );
    } else {
      dispatch(actions.setValue({ name: 'syncModel.HivePublishConnection', value: null, sync: true }));
    }
  };

  return (
    <ListFieldSection single>
      <Field
        input={<Checkbox />}
        name="syncModel.HivePublishConnection"
        label="Construction Hive Publishing"
        onChange={(e) => addRemoveHiveConnection(e.data.value)}
        confirm={({ value }) => value !== true}
        confirmText={`Are you sure you want to delete the Hive Publishing connection?`}
        confirmButtonText="Delete Connection"
        confirmTitle="Delete Hive Publishing Connection"
      />
      <Field
        input={<Checkbox />}
        name="showCustomIntegrations"
        label="Custom Integrators"
        value={showSection}
        onChange={(e) => handleClick(e.target.checked)}
        disabled={disableCheckbox}
      />
    </ListFieldSection>
  );
};

const QESTPlatformConnectionsSection = () => {
  const connection = useSelector((state) => state.admin.syncModel.QESTPlatformConnection);
  return (
    <PageSection>
      <ListFieldSection single heading="QEST Platform">
        {!connection ? (
          <Message error>No database connections found</Message>
        ) : (
          <DatabaseConnectionSection connection={connection} connectionName="QESTPlatformConnection" />
        )}
      </ListFieldSection>
    </PageSection>
  );
};

const HivePublishConnectionSection = () => {
  const connection = useSelector((state) => state.admin.syncModel.HivePublishConnection);
  return connection ? (
    <PageSection>
      <ListFieldSection single heading="Construction Hive Publishing">
        <DatabaseConnectionSection connection={connection} connectionName="HivePublishConnection" />
      </ListFieldSection>
    </PageSection>
  ) : null;
};

const IntegrationConnectionsSection = () => {
  const connections = useSelector((state) => state.admin.syncModel.IntegrationDbConnections);
  const path = useSelector((state) => state.admin.databaseConnectionTest.path);
  const testing = useSelector((state) => state.admin.databaseConnectionTest.testing);
  const dispatch = useDispatch();

  const removeConnection = (index) => {
    dispatch(
      actions.setValue({
        name: 'syncModel.IntegrationDbConnections',
        value: connections.filter((_, i) => i !== index),
        sync: true,
      })
    );
  };

  const addConnection = () => {
    const exists = (name) => {
      return connections.some((c) => c.Name === name);
    };
    let index = connections.length;
    let name = `NewConnection_${index}`;
    while (exists(name)) {
      index = index + 1;
      name = `NewConnection_${index}`;
    }

    dispatch(
      actions.setValue({
        name: 'syncModel.IntegrationDbConnections',
        value: [
          ...connections,
          {
            Name: name,
            DataSource: '',
            InitialCatalog: '',
            IntegratedSecurity: 'true',
            IsAssisted: 'true',
            CustomerProviderSelection: 'System.Data.SqlClient',
          },
        ],
        sync: true,
      })
    );
  };

  const customConnections = () => {
    if (!connections) return [];

    return connections.map((_, i) => [
      <FieldSet key={i}>
        <DatabaseConnectionSection
          connection={_}
          connectionName={`IntegrationDbConnections[${i}]`}
          setName
          hideProviderSelection={false}
        />
        <ButtonGroup align="center">
          <ConfirmDialog
            confirm
            confirmText={`Are you sure you want to delete the integration connection '${_.Name}'?`}
            confirmButtonText="Delete Connection"
            confirmTitle="Delete Integration Connection"
          >
            <Button key={i} subtle error disabled={testing && path === _.Name} onClick={() => removeConnection(i)}>
              <MdRemoveCircle size="2em" />
            </Button>
          </ConfirmDialog>
        </ButtonGroup>
      </FieldSet>,
    ]);
  };

  return (
    <ListFieldSection heading="Custom Integrators" single>
      {customConnections()}
      <div style={{ height: 190, display: 'flex' }}>
        <PlaceholderButton style={{ height: 170 }} onClick={() => addConnection()} />
      </div>
    </ListFieldSection>
  );
};

const DatabaseConnectionsAdmin = () => {
  const connections = useSelector((state) => state.admin.syncModel.IntegrationDbConnections);
  const [showSection, setShowSection] = useState(false);
  const handleCheckbox = (value) => {
    setShowSection(value);
  };

  //We want the Integration Section to show on load if there are already defined connections
  useEffect(() => {
    if (!showSection) setShowSection(connections ? connections.length : false);
  }, [showSection, connections]);

  return (
    <>
      <PageSection>
        <Message warning>
          Changes to database connections will require a <Link to="/admin/restart">QESTNet restart.</Link>
        </Message>
        <FilterSection handleClick={handleCheckbox} showSection={showSection} />
      </PageSection>
      <QESTPlatformConnectionsSection />
      <HivePublishConnectionSection />
      {showSection ? (
        <PageSection>
          <IntegrationConnectionsSection />
        </PageSection>
      ) : null}
    </>
  );
};

export default DatabaseConnectionsAdmin;
export { DatabaseConnectionsAdmin };
